const USERNAME_MIN = 7;
const USERNAME_MAX = 20;
const PASSWORD_MIN = 10;

const RESERVED_USERNAMES = new Set([
  'admin', 'administrator', 'root', 'system', 'support', 'suporte',
  'moderator', 'moderador', 'mod', 'staff', 'oficial', 'official',
  'vihs', 'vihsbot', 'null', 'undefined', 'api', 'www', 'help',
  'security', 'seguranca',
]);

export function evaluateUsernameClient(username) {
  const value = String(username || '').trim();
  const checks = {
    length: value.length >= USERNAME_MIN && value.length <= USERNAME_MAX,
    startsWithLetter: /^[a-zA-Z]/.test(value),
    charset: /^[a-zA-Z0-9._-]*$/.test(value) && Boolean(value),
    noEdgeSpecial: Boolean(value) && !/[._-]$/.test(value) && !/[._-]{2,}/.test(value),
    available: !RESERVED_USERNAMES.has(value.toLowerCase()),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const max = Object.keys(checks).length;
  return { value, checks, score, max, ok: score === max };
}

function hasSequentialRun(value, size = 3) {
  const lower = value.toLowerCase();
  const sequences = 'abcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i <= lower.length - size; i += 1) {
    const chunk = lower.slice(i, i + size);
    if (sequences.includes(chunk)) return true;
    if (sequences.includes([...chunk].reverse().join(''))) return true;
  }
  return false;
}

function containsPersonalData(password, context = {}) {
  const lowered = String(password).toLowerCase();
  const parts = [];
  if (context.username) parts.push(String(context.username).toLowerCase());
  if (context.email) {
    const local = String(context.email).toLowerCase().split('@')[0];
    if (local) parts.push(local);
  }
  return parts
    .filter((part) => part && part.length >= 3)
    .some((part) => lowered.includes(part) || lowered.includes(part.replace(/[._-]/g, '')));
}

export function evaluatePasswordClient(password, context = {}) {
  const value = String(password || '');
  const checks = {
    length: value.length >= PASSWORD_MIN,
    lower: /[a-z]/.test(value),
    upper: /[A-Z]/.test(value),
    number: /[0-9]/.test(value),
    special: /[^A-Za-z0-9]/.test(value),
    noSpaces: value.length > 0 && !/\s/.test(value),
    noSequence: value.length > 0 && !hasSequentialRun(value),
    noRepeat: value.length > 0 && !/(.)\1{2,}/.test(value),
    noPersonal: value.length > 0 && !containsPersonalData(value, context),
  };

  const required = ['length', 'lower', 'upper', 'number', 'special', 'noSpaces'];
  const extras = ['noSequence', 'noRepeat', 'noPersonal'];
  const requiredOk = required.every((key) => checks[key]);
  const extrasOk = extras.every((key) => checks[key]);
  const score = required.filter((key) => checks[key]).length
    + extras.filter((key) => checks[key]).length;
  const max = required.length + extras.length;

  let label = 'Muito fraca';
  if (requiredOk && extrasOk) label = 'Excelente';
  else if (score >= 7) label = 'Forte';
  else if (score >= 5) label = 'Média';
  else if (score >= 3) label = 'Fraca';

  return {
    checks,
    score,
    max,
    ok: requiredOk && extrasOk,
    label,
  };
}

export const AUTH_POLICY = {
  USERNAME_MIN,
  USERNAME_MAX,
  PASSWORD_MIN,
};

export const USERNAME_CHECK_LABELS = {
  length: `Pelo menos ${USERNAME_MIN} caracteres`,
  startsWithLetter: 'Começa com letra',
  charset: 'Apenas letras, números, . _ -',
  noEdgeSpecial: 'Sem especiais no fim ou em sequência',
  available: 'Nome permitido pelo sistema',
};

export const PASSWORD_CHECK_LABELS = {
  length: `Pelo menos ${PASSWORD_MIN} caracteres`,
  lower: 'Uma letra minúscula',
  upper: 'Uma letra maiúscula',
  number: 'Um número',
  special: 'Um símbolo (!@#$%...)',
  noSpaces: 'Sem espaços',
  noSequence: 'Sem sequências óbvias (abc, 123)',
  noRepeat: 'Sem repetição (aaa, 111)',
  noPersonal: 'Sem usuário ou e-mail na senha',
};
